import type { BreakdownItem, BreakdownSubItem } from '@/types/analytics'
import { cn } from '@/utils/cn'
import { NETWORKS, type Network } from '@vaultsfyi/common/types'
import { formatDecimalToPercentage, formatUSD } from '@vaultsfyi/common/utils'
import { BreakdownChart } from '../charts/breakdownChart'
import { ChartTooltip } from '../charts/chartTooltip'
import { Separator } from '../common/separator'

const OTHERS_TOOLTIP_THRESHOLD = 500_000_000

function SubItemRow({ item }: { item: BreakdownSubItem }) {
  return (
    <p className="flex justify-between gap-2">
      <span
        className={cn(
          'w-26 truncate whitespace-nowrap font-semibold',
          NETWORKS.includes(item.name as Network) && 'first-letter:uppercase'
        )}
      >
        {item.displayName}
      </span>{' '}
      <span>{formatUSD(item.value)}</span>
    </p>
  )
}

function TvlBreakdownTooltip({ data }: { data: BreakdownItem | undefined }) {
  if (!data) return null

  if (data.subItems?.length) {
    const above = data.subItems.filter((sub) => sub.value >= OTHERS_TOOLTIP_THRESHOLD)
    const below = data.subItems.filter((sub) => sub.value < OTHERS_TOOLTIP_THRESHOLD)
    const belowSum = below.reduce((sum, sub) => sum + sub.value, 0)

    return (
      <ChartTooltip className="flex w-max flex-col gap-2">
        <p className="flex gap-5">
          <span className="w-26 truncate font-semibold">Others:</span>{' '}
          <span>
            {formatUSD(data.value)} ({formatDecimalToPercentage(data.percentage)})
          </span>
        </p>
        {above.length > 0 && (
          <>
            <Separator className="bg-border" />
            <div
              data-cols={Math.min(above.length, 3)}
              className="grid gap-x-8 gap-y-0.5 data-[cols=1]:grid-cols-1 data-[cols=2]:grid-cols-2 data-[cols=3]:grid-cols-3"
            >
              {above.map((sub) => (
                <SubItemRow key={sub.name} item={sub} />
              ))}
            </div>
          </>
        )}
        {below.length > 0 && (
          <>
            <Separator className="bg-border" />
            <p className="flex gap-5">
              <span className="w-26 truncate font-semibold">Rest:</span> <span>{formatUSD(belowSum)}</span>
            </p>
          </>
        )}
      </ChartTooltip>
    )
  }

  return (
    <ChartTooltip>
      <p className="flex justify-between gap-2">
        <span className={cn('whitespace-nowrap', NETWORKS.includes(data.name as Network) && 'first-letter:uppercase')}>
          {data.displayName}
        </span>{' '}
        <span>
          {formatUSD(data.value)} ({formatDecimalToPercentage(data.percentage)})
        </span>
      </p>
    </ChartTooltip>
  )
}

export function TvlBreakdownChart({
  items,
  size,
  className,
}: { items: BreakdownItem[]; size?: number; className?: string }) {
  return (
    <BreakdownChart
      items={items}
      size={size}
      className={className}
      renderTooltip={(data) => <TvlBreakdownTooltip data={data} />}
    />
  )
}
